import { flowService } from 'v2/shared/services/flow-service'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { useVibeAgentStore } from './store'
import { attachViewer, isViewerActive, type StreamCallbacks } from './transport'
import { type ConversationSnapshot, isRunLive, type RunStatus } from './wire'

/**
 * Snapshot sync: mount rehydration and stream_gap resync are the SAME operation —
 * pull the authoritative conversation, hydrate the store through applySnapshot
 * (which merges the local pending tail, so a resync can never drop a just-sent
 * message), then re-attach the viewer if a run is live and no reader exists.
 * It never detaches an in-flight reader: transport.attachViewer is a no-op while
 * one is active.
 *
 * The fetch is async, so the caller passes `isCurrent`: a liveness/scope guard
 * checked AFTER the await and BEFORE any store/viewer write. If the flow switched
 * or the hook unmounted while the snapshot was in flight, we drop it — hydrating
 * would contaminate the new flow's store and attachViewer would spawn a zombie
 * reader on a torn-down viewer.
 */

/** A snapshot may hydrate only when no live local reader exists, or when the
 *  snapshot itself is of the live run. Without this, a mount-resync racing a
 *  just-sent message lands a STALE pre-run snapshot after the run went live —
 *  wiping streamed assistant rows, resetting runStatus to idle (composer
 *  unlocks → next send 409s) and nulling responseId (cancel dead). Pure —
 *  unit tested. */
export const shouldHydrateSnapshot = (viewerActive: boolean, status: RunStatus | null): boolean =>
  !viewerActive || isRunLive(status)

export const fetchConversation = async (phloUuid: string): Promise<ConversationSnapshot | null> => {
  try {
    const res = await flowService.getVibeAgentConversation(phloUuid)
    // aiassist wraps in the ApiResponse envelope; payload under .data.
    return res?.data ?? null
  } catch (e) {
    logger.warn('[VIBE_SYNC] conversation fetch failed', e)
    return null
  }
}

export const resync = async (
  phloUuid: string,
  viewer: StreamCallbacks,
  isCurrent: () => boolean,
): Promise<ConversationSnapshot | null> => {
  const snapshot = await fetchConversation(phloUuid)
  if (!snapshot) return null
  if (!isCurrent()) return null

  if (!shouldHydrateSnapshot(isViewerActive(), snapshot.run_status)) {
    // A live local run owns the truth — dropping the stale snapshot also skips
    // the caller's canvas reconcile (null return), which would otherwise
    // delete-reconcile freshly streamed nodes off the canvas.
    logger.info('[VIBE_SYNC] skipped stale snapshot during live run')
    return null
  }

  useVibeAgentStore.getState().hydrate(snapshot)

  if (isRunLive(snapshot.run_status) && snapshot.session_id && !isViewerActive()) {
    attachViewer(snapshot.session_id, snapshot.last_stream_id, viewer)
  }
  return snapshot
}
